import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, CalendarCheck, Building2, Users, LogOut } from "lucide-react";
import { getLeads, getConsultations, getStats } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-black/10 bg-white p-6" data-testid={`stat-${label}`}>
    <Icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
    <p className="mt-4 font-display text-3xl text-ink">{value}</p>
    <p className="mt-1 text-xs uppercase tracking-wider text-black/45">{label}</p>
  </div>
);

const fmt = (t) => new Date(t).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const Admin = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [consults, setConsults] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    getLeads().then(setLeads).catch(() => {});
    getConsultations().then(setConsults).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-paper px-6 py-12 sm:px-10 lg:px-16" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-gold mb-3">Sales Dashboard</p>
            <h1 className="font-display text-4xl text-ink">Lead & Consultation Console</h1>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn" className="rounded-none border-black/15">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Mail} label="Total Leads" value={stats?.total_leads ?? "—"} />
          <StatCard icon={Building2} label="Corporate Leads" value={stats?.corporate_leads ?? "—"} />
          <StatCard icon={Users} label="Personal Leads" value={stats?.personal_leads ?? "—"} />
          <StatCard icon={CalendarCheck} label="Consultations" value={stats?.total_consultations ?? "—"} />
        </div>

        <Tabs defaultValue="leads" className="mt-12">
          <TabsList className="rounded-none bg-black/5">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none">Guide Leads</TabsTrigger>
            <TabsTrigger value="consults" data-testid="tab-consults" className="rounded-none">Consultations</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="mt-6 border border-black/10 bg-white">
              {leads.length === 0 && <p className="p-8 text-center text-sm text-black/40">No leads yet.</p>}
              {leads.map((l) => (
                <div key={l.id} className="flex items-center justify-between border-b border-black/5 px-6 py-4 last:border-0" data-testid="lead-row">
                  <div>
                    <p className="font-medium text-ink">{l.email}</p>
                    <p className="text-xs text-black/40">{fmt(l.timestamp)} · {l.source}</p>
                  </div>
                  <Badge className={`rounded-none ${l.is_corporate ? "bg-gold text-ink" : "bg-black/10 text-black/60"}`}>
                    {l.is_corporate ? "Corporate" : "Personal"}
                  </Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="consults">
            <div className="mt-6 space-y-3">
              {consults.length === 0 && <p className="border border-black/10 bg-white p-8 text-center text-sm text-black/40">No consultations yet.</p>}
              {consults.map((c) => (
                <div key={c.id} className="border border-black/10 bg-white p-6" data-testid="consult-row">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-lg text-ink">{c.name}</p>
                    <Badge className={`rounded-none ${c.is_corporate ? "bg-gold text-ink" : "bg-black/10 text-black/60"}`}>
                      {c.is_corporate ? "Corporate" : "Personal"}
                    </Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-1 text-sm text-black/60 sm:grid-cols-2">
                    <p>{c.email}</p>
                    <p>{c.phone}</p>
                    <p>Preferred: <span className="text-ink">{c.preferred_date}</span></p>
                    <p>Interest: <span className="text-ink">{c.package_interest || "—"}</span></p>
                    {c.season && <p>Season: <span className="text-ink">{c.season}</span></p>}
                  </div>
                  {c.message && <p className="mt-2 text-sm italic text-black/50">{`\u201C${c.message}\u201D`}</p>}
                  <p className="mt-2 text-xs text-black/35">{fmt(c.timestamp)}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
